import { message } from 'antd'
import { getInvoices, getPatientPayments, invoicesAggregate, countInvoices } from '../utils'
import {
  createFollowUpAccount,
  bulkCreateFollowUpAccounts,
  createLedgerTransaction,
  getFollowUpAccounts,
  countFollowUpAccounts
} from '../../Billing-Module-Enhanced/backend-integration/api'
import { readPatients } from '../../Admin/patients/utils/utils'
import store from '../../../store'
import dayjs from 'dayjs'
import BillingEnums from '../../Billing-Module-Enhanced/utils/enums'

/**
 * Sync invoices data with billing follow-up accounts
 * @param {Object} options - Sync options
 * @param {boolean} options.onlyOutstanding - Only sync invoices with outstanding balances
 * @param {string} options.dateFrom - Start date for filtering invoices
 * @param {string} options.dateTo - End date for filtering invoices
 * @returns {Promise} Sync result
 */
export const syncInvoicesToBilling = async (options = {}) => {
  const { onlyOutstanding = true, dateFrom, dateTo } = options

  try {
    message.loading('Starting invoice sync...', 0)

    // Build filter for invoices
    const invoiceFilter = {
      entityRef: store.getState().core.entity._id
    }

    if (onlyOutstanding) {
      invoiceFilter.status = { $ne: 'completed' }
    }

    if (dateFrom || dateTo) {
      invoiceFilter.createdAt = {}
      if (dateFrom) invoiceFilter.createdAt.$gte = dateFrom
      if (dateTo) invoiceFilter.createdAt.$lte = dateTo
    }

    // Fetch all invoices with aggregation to get patient and balance info
    const pipeline = [
      { $match: invoiceFilter },
      {
        $lookup: {
          from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_patient_payments`,
          let: { invoiceId: { $toString: '$_id' } },
          pipeline: [
            {
              $match: {
                $expr: { $eq: ['$invoiceRef', '$$invoiceId'] }
              }
            }
          ],
          as: 'payments'
        }
      },
      {
        $lookup: {
          from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_billing_records`,
          localField: 'bookingRef',
          foreignField: 'bookingRef',
          as: 'billingRecords'
        }
      },
      {
        $addFields: {
          totalAmount: {
            $sum: {
              $map: {
                input: '$billingRecords',
                as: 'record',
                in: {
                  $add: [
                    {
                      $cond: {
                        if: { $eq: [{ $type: '$$record.medicalAidDue' }, 'string'] },
                        then: { $toDouble: '$$record.medicalAidDue' },
                        else: { $ifNull: ['$$record.medicalAidDue', 0] }
                      }
                    },
                    {
                      $cond: {
                        if: { $eq: [{ $type: '$$record.patientDue' }, 'string'] },
                        then: { $toDouble: '$$record.patientDue' },
                        else: { $ifNull: ['$$record.patientDue', 0] }
                      }
                    }
                  ]
                }
              }
            }
          },
          medicalAidAmount: {
            $sum: {
              $map: {
                input: '$billingRecords',
                as: 'record',
                in: {
                  $cond: {
                    if: { $eq: [{ $type: '$$record.medicalAidDue' }, 'string'] },
                    then: { $toDouble: '$$record.medicalAidDue' },
                    else: { $ifNull: ['$$record.medicalAidDue', 0] }
                  }
                }
              }
            }
          },
          patientAmount: {
            $sum: {
              $map: {
                input: '$billingRecords',
                as: 'record',
                in: {
                  $cond: {
                    if: { $eq: [{ $type: '$$record.patientDue' }, 'string'] },
                    then: { $toDouble: '$$record.patientDue' },
                    else: { $ifNull: ['$$record.patientDue', 0] }
                  }
                }
              }
            }
          },
          totalPaid: {
            $sum: {
              $map: {
                input: '$payments',
                as: 'payment',
                in: {
                  $cond: {
                    if: { $eq: [{ $type: '$$payment.paymentAmount' }, 'string'] },
                    then: { $toDouble: '$$payment.paymentAmount' },
                    else: '$$payment.paymentAmount'
                  }
                }
              }
            }
          }
        }
      },
      {
        $addFields: {
          // For medical aid patients, outstanding is only the patient portion minus payments
          // For private patients, outstanding is the total amount minus payments
          outstandingAmount: {
            $cond: {
              if: { $gt: ['$medicalAidAmount', 0] },
              then: { $subtract: ['$patientAmount', '$totalPaid'] },
              else: { $subtract: ['$totalAmount', '$totalPaid'] }
            }
          }
        }
      }
    ]

    const invoicesWithBalances = await invoicesAggregate(pipeline)

    if (!invoicesWithBalances || invoicesWithBalances.length === 0) {
      message.warning('No invoices found to sync')
      return { success: true, synced: 0 }
    }

    console.log('Invoices with balances:', invoicesWithBalances) // Debug log to check data

    // Get unique patient IDs
    const patientIds = [...new Set(invoicesWithBalances.map((inv) => inv.userRef).filter(Boolean))]

    // Fetch patient details
    const patients = await readPatients({ _id: { $in: patientIds } }, {}, 'firstName lastName idNo fileNo medicalAid')

    const patientMap = new Map(patients.map((p) => [p._id, p]))

    // Check existing active follow-up accounts to avoid duplicates
    const existingAccountsResponse = await getFollowUpAccounts(
      { status: { $ne: 'completed' } }, // Only get non-completed accounts
      1,
      10000,
      'billingId patientId'
    )
    const existingAccounts = Array.isArray(existingAccountsResponse)
      ? existingAccountsResponse
      : existingAccountsResponse.data || []

    // Create a Set of patientId+billingId combinations for active accounts
    const existingActiveAccounts = new Set(existingAccounts.map((acc) => `${acc.patientId}_${acc.billingId}`))

    // Prepare follow-up accounts and ledger transactions
    const followUpAccounts = []
    const ledgerTransactions = []

    for (const invoice of invoicesWithBalances) {
      if (!invoice.userRef) continue

      const patient = patientMap.get(invoice.userRef)
      if (!patient) continue

      // Skip if already has active follow-up account
      const patientBillingId = patient.billingId
      if (!patientBillingId) {
        console.error(`Patient ${patient.firstName} ${patient.lastName} (${patient._id}) does not have a billingId`)
        continue // Skip this invoice if patient doesn't have billingId
      }

      if (existingActiveAccounts.has(`${invoice.userRef}_${patientBillingId}`)) {
        continue
      }

      // Calculate days outstanding
      const invoiceDate = dayjs(invoice.createdAt)
      const daysOutstanding = dayjs().diff(invoiceDate, 'day')

      // Determine aging bucket
      let agingBucket = '0-29'
      if (daysOutstanding >= 90) agingBucket = '90+'
      else if (daysOutstanding >= 60) agingBucket = '60-89'
      else if (daysOutstanding >= 30) agingBucket = '30-59'

      // Determine account group based on invoice type
      let accountGroup = 'private'
      if (invoice.isMedicalAid || patient.medicalAid?.name) {
        accountGroup = 'medical_aid'
      }

      // Determine what's outstanding and set appropriate status
      const hasOutstandingPatientAmount = (invoice.patientAmount || 0) > (invoice.totalPaid || 0)
      const hasOutstandingMedicalAidAmount = (invoice.medicalAidAmount || 0) > 0

      let status = 'completed'
      if (hasOutstandingPatientAmount && hasOutstandingMedicalAidAmount) {
        // Both patient and medical aid portions are outstanding
        status = 'awaiting_payment_aid' // Medical aid takes priority
      } else if (hasOutstandingPatientAmount) {
        // Only patient portion is outstanding
        status = 'awaiting_payment_patient'
      } else if (hasOutstandingMedicalAidAmount) {
        // Only medical aid portion is outstanding
        status = 'awaiting_payment_aid'
      }

      // Create follow-up account with separate outstanding amounts
      const billingId = patient.billingId
      if (!billingId) {
        console.error(`Patient ${patient.firstName} ${patient.lastName} (${patient._id}) does not have a billingId`)
        continue // Skip this invoice if patient doesn't have billingId
      }
      const followUpAccount = {
        billingId: billingId,
        billindId: billingId, // TEMPORARY: Remove this after fixing MongoDB index
        accountNo: billingId, // accountNo is required and should match billingId
        patientId: invoice.userRef,
        patientName: `${patient.firstName} ${patient.lastName}`,
        fileNo: patient.fileNo || '',
        medicalAid: patient.medicalAid?.name || '',
        outstandingAmount: Math.max(0, invoice.outstandingAmount || 0),
        patientOutstanding: Math.max(0, (invoice.patientAmount || 0) - (invoice.totalPaid || 0)),
        medicalAidOutstanding: Math.max(0, invoice.medicalAidAmount || 0),
        daysOutstanding,
        agingBucket,
        status,
        accountGroup,
        lastInvoiceDate: invoice.createdAt,
        nextFollowUpDate: dayjs().add(7, 'day').toDate(),
        remittanceStatus: invoice.medicalAidAmount > 0 ? 'pending' : 'paid',
        followUpHistory: [],
        statusHistory: [
          {
            status: 'created_from_sync',
            date: new Date().toISOString(),
            user: store.getState().auth.agiliteUser?._id,
            notes: 'Account created from invoice sync',
            invoiceRef: invoice._id
          }
        ]
      }

      followUpAccounts.push(followUpAccount)

      // Create initial ledger transaction for the invoice
      const ledgerTransaction = {
        patientId: invoice.userRef,
        billingId: followUpAccount.billingId,
        date: invoice.createdAt,
        type: BillingEnums.transactionTypes.INVOICE,
        description: `Invoice ${invoice.invoiceNo}`,
        invoiceNo: invoice.invoiceNo,
        debit: invoice.totalAmount || 0,
        credit: 0,
        status: invoice.status,
        medicalAidAmount: invoice.medicalAidAmount || 0,
        patientAmount: invoice.patientAmount || 0,
        notes: `Initial invoice from sync - Medical Aid: R${(invoice.medicalAidAmount || 0).toFixed(2)}, Patient: R${(
          invoice.patientAmount || 0
        ).toFixed(2)}`
      }

      ledgerTransactions.push(ledgerTransaction)

      // Add payment transactions if any
      if (invoice.payments && invoice.payments.length > 0) {
        for (const payment of invoice.payments) {
          const paymentTransaction = {
            patientId: invoice.userRef,
            billingId: followUpAccount.billingId,
            date: payment.receiptDate || payment.createdAt,
            type: BillingEnums.transactionTypes.PAYMENT,
            description: `Payment - ${payment.paymentMethod || 'Unknown'}`,
            invoiceNo: invoice.invoiceNo,
            debit: 0,
            credit: parseFloat(payment.paymentAmount) || 0,
            status: 'Processed',
            paymentMethod: payment.paymentMethod,
            reference: payment.receiptNumber,
            notes: 'Payment from invoice sync'
          }

          ledgerTransactions.push(paymentTransaction)
        }
      }
    }

    if (followUpAccounts.length === 0) {
      message.info('All invoices already have follow-up accounts')
      return { success: true, synced: 0 }
    }

    // Bulk create follow-up accounts
    await bulkCreateFollowUpAccounts(followUpAccounts)

    // Create ledger transactions
    for (const transaction of ledgerTransactions) {
      await createLedgerTransaction(transaction)
    }

    message.destroy()
    message.success(`Successfully synced ${followUpAccounts.length} invoices to billing module`)

    return {
      success: true,
      synced: followUpAccounts.length,
      totalTransactions: ledgerTransactions.length
    }
  } catch (error) {
    message.destroy()
    message.error('Failed to sync invoices: ' + error.message)
    console.error('Sync error:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

/**
 * Get sync status and statistics
 * @returns {Promise} Sync statistics
 */
export const getSyncStatus = async () => {
  try {
    const [invoiceCount, accountCount] = await Promise.all([
      countInvoices({ status: { $ne: 'completed' } }),
      countFollowUpAccounts({})
    ])

    return {
      totalInvoices: invoiceCount,
      totalFollowUpAccounts: accountCount,
      unsynced: Math.max(0, invoiceCount - accountCount)
    }
  } catch (error) {
    console.error('Error getting sync status:', error)
    return {
      totalInvoices: 0,
      totalFollowUpAccounts: 0,
      unsynced: 0
    }
  }
}
